import { useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { Stars } from "./stars";
import { ReviewsDialog } from "./reviews-dialog";
import { formatarNota } from "./format";
import { useLandingReviews } from "./use-reviews";
import { buttonClass } from "../ui-tokens";

const NOTAS = [5, 4, 3, 2, 1] as const;

/**
 * Bloco de nota com distribuição, para dentro da seção de Depoimentos.
 *
 * Repete a distribuição que o modal também mostra — não é redundância: aqui ela
 * é a isca (a barra desenhada convida o clique), e no modal ela é o filtro. É o
 * mesmo par isca/painel que o Google usa no cartão de avaliação.
 *
 * As barras não crescem mais ao entrar na tela: animavam `width` (layout a
 * cada quadro) para um dado que o olho lê parado.
 */
export function RatingPanel({ className }: { className?: string }) {
  const { data, suficiente } = useLandingReviews();
  const [aberto, setAberto] = useState(false);

  if (!data || !suficiente) return null;

  return (
    <>
      <div
        className={cn(
          "lp-reveal lp-surface p-6 md:p-7",
          "flex flex-col gap-6 sm:flex-row sm:items-center sm:gap-8",
          className,
        )}
      >
        {/* ── Média ────────────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center gap-4 sm:flex-col sm:gap-1.5">
          <span className="text-5xl leading-none font-semibold tracking-tight text-foreground tabular-nums">
            {formatarNota(data.average)}
          </span>
          <div className="flex flex-col gap-1.5 sm:items-center">
            <Stars value={data.average} size="lg" />
            <span className="text-xs whitespace-nowrap text-muted-foreground">
              {data.total} {data.total === 1 ? "avaliação" : "avaliações"}
            </span>
          </div>
        </div>

        <div
          className="hidden w-px shrink-0 self-stretch bg-border sm:block"
          aria-hidden="true"
        />

        {/* ── Distribuição ─────────────────────────────────────────── */}
        <ul className="min-w-0 flex-1 space-y-1.5" aria-label="Avaliações por nota">
          {NOTAS.map((nota) => {
            const qtd = data.distribution[nota] || 0;
            const pct = data.total > 0 ? (qtd / data.total) * 100 : 0;
            return (
              <li key={nota} className="flex items-center gap-2.5">
                <span className="flex w-7 shrink-0 items-center gap-0.5 text-xs text-muted-foreground tabular-nums">
                  {nota}
                  <Star
                    className="size-3 fill-lp-rating text-lp-rating"
                    aria-hidden="true"
                  />
                  <span className="sr-only">
                    {nota === 1 ? "estrela" : "estrelas"}:
                  </span>
                </span>
                <span
                  className="h-2 flex-1 overflow-hidden rounded-full bg-secondary"
                  aria-hidden="true"
                >
                  <span
                    className="block h-full rounded-full bg-lp-rating"
                    style={{ width: `${pct}%` }}
                  />
                </span>
                <span className="w-6 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                  {qtd}
                </span>
              </li>
            );
          })}
        </ul>

        {/* ── Chamada para o modal ─────────────────────────────────── */}
        {/* Secundário: na seção de depoimentos a ação principal da página
            continua sendo o cadastro. */}
        <div className="flex shrink-0 items-center gap-3 sm:flex-col sm:border-l sm:border-border sm:pl-7">
          <button
            type="button"
            onClick={() => setAberto(true)}
            className={buttonClass("secondary")}
          >
            Ler os comentários
          </button>
          <p className="hidden max-w-28 text-center text-xs leading-snug text-muted-foreground sm:block">
            Avaliação só de quem tem conta ativa
          </p>
        </div>
      </div>

      <ReviewsDialog open={aberto} onOpenChange={setAberto} data={data} />
    </>
  );
}
